import { SyncIntegrationNames } from 'common/constants/autopilotIntegrations';
import { IntegrationNames, isIntegrationName } from 'common/constants/integrations';
import Via, { isVia } from 'common/constants/via';
import unique from 'common/util/unique';

export enum ManualSourceNames {
  UI = 'ui',
  API = 'api',
  Extension = 'extension',
  Unknown = 'unknown',
}

export enum SourceTypes {
  Autopilot = 'autopilot',
  Manual = 'manual',
}

export const SourcesList = unique([
  ...Object.values(SyncIntegrationNames),
  ...Object.values(ManualSourceNames),
  ...Object.values(IntegrationNames),
]);

export type Source =
  | {
      name: SyncIntegrationNames;
      type: SourceTypes.Autopilot;
    }
  | {
      name: IntegrationNames | ManualSourceNames;
      type: SourceTypes.Manual;
    };

export const validateSource = (
  source: Source
): { success: true; error: null } | { success: false; error: string } => {
  // validate source shape
  if (!source || typeof source !== 'object' || !('type' in source) || !('name' in source)) {
    return {
      success: false,
      error: 'Invalid source: missing required properties',
    };
  }

  // validate source content
  const ValidNamesMap: Record<SourceTypes, string[]> = {
    [SourceTypes.Autopilot]: Object.values(SyncIntegrationNames),
    [SourceTypes.Manual]: [...Object.values(ManualSourceNames), ...Object.values(IntegrationNames)],
  };

  if (!ValidNamesMap[source.type]) {
    return {
      success: false,
      error: 'Invalid source type',
    };
  } else if (!ValidNamesMap[source.type].includes(source.name)) {
    return {
      success: false,
      error: 'Invalid source: name does not match type',
    };
  }

  return {
    success: true,
    error: null,
  };
};

export const DefaultSource: Source = {
  name: ManualSourceNames.Unknown,
  type: SourceTypes.Manual,
} as const;

/**
 * Returns a source object from an origin string.
 *
 * @param origin - Either a "Via" or an "IntegrationName".
 * @returns A source object, defaults to "Unknown" if the origin is not found.
 */
export const getManualSourceFromOrigin = (origin: Via | IntegrationNames | null): Source => {
  const ViaMap: Record<string, Source> = {
    [Via.api]: {
      name: ManualSourceNames.API,
      type: SourceTypes.Manual,
    },
    [Via.browserExtension]: {
      name: ManualSourceNames.Extension,
      type: SourceTypes.Manual,
    },
    [Via.user]: {
      name: ManualSourceNames.UI,
      type: SourceTypes.Manual,
    },
  };

  if (!origin) {
    return DefaultSource;
  } else if (isIntegrationName(origin)) {
    return {
      name: origin,
      type: SourceTypes.Manual,
    };
  } else if (isVia(origin)) {
    return ViaMap[origin] ?? DefaultSource;
  }

  return DefaultSource;
};

/**
 * Type guard to check if object is a source
 */
export function isSource(source: Record<string, unknown>): source is Source {
  return (
    source &&
    typeof source === 'object' &&
    'type' in source &&
    'name' in source &&
    [SourceTypes.Manual, SourceTypes.Autopilot].includes(source.type as SourceTypes)
  );
}
